/**
 * Centralized API Error Handler
 * Provides consistent error handling across all API calls
 */

import { toast } from 'react-toastify';

import { removeAuthToken } from './auth';

export interface ApiErrorDetails {
  message: string;
  status?: number;
  code?: string;
  details?: any;
  isAuthError: boolean;
  isNetworkError: boolean;
}

/**
 * Extract detailed error information from various error formats
 */
export function parseApiError(error: any): ApiErrorDetails {
  const details: ApiErrorDetails = {
    message: 'An unexpected error occurred',
    isAuthError: false,
    isNetworkError: false,
  };

  // Network errors
  if (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    error.message === 'Network Error'
  ) {
    details.message = 'Network error. Please check your connection.';
    details.isNetworkError = true;
    details.code = error.code;
    return details;
  }

  // Axios errors with response
  if (error.response) {
    details.status = error.response.status;

    // Authentication errors
    if (error.response.status === 401) {
      details.message = 'Your session has expired. Please log in again.';
      details.isAuthError = true;
      details.code = 'AUTH_REQUIRED';
    }
    // Authorization errors
    else if (error.response.status === 403) {
      details.message = 'You do not have permission to perform this action.';
      details.code = 'FORBIDDEN';
    }
    // Not found errors
    else if (error.response.status === 404) {
      details.message = 'The requested resource was not found.';
      details.code = 'NOT_FOUND';
    }
    // Validation errors
    else if (error.response.status === 400) {
      details.message =
        error.response.data?.message || error.response.data?.error || 'Invalid request data.';
      details.code = 'VALIDATION_ERROR';
    }
    // Server errors
    else if (error.response.status >= 500) {
      details.message = 'Server error. Please try again later.';
      details.code = 'SERVER_ERROR';
    }
    // Other errors
    else {
      details.message =
        error.response.data?.message ||
        error.response.data?.error ||
        error.message ||
        'Request failed';
    }

    // Store additional details
    details.details = error.response.data;
  }
  // Axios errors without response (request was made but no response)
  else if (error.request) {
    details.message = 'No response from server. Please check your connection.';
    details.isNetworkError = true;
    details.code = 'NO_RESPONSE';
  }
  // Other errors
  else if (error.message) {
    details.message = error.message;
  }

  return details;
}

/**
 * Handle API error with appropriate user notification
 */
export function handleApiError(
  error: any,
  customMessage?: string,
  options: {
    silent?: boolean;
    showDetails?: boolean;
    redirectOnAuth?: boolean;
  } = {}
): ApiErrorDetails {
  const { silent = false, showDetails = false, redirectOnAuth = true } = options;

  // Parse error
  const errorDetails = parseApiError(error);

  // Use custom message if provided
  if (customMessage) {
    errorDetails.message = customMessage;
  }

  // Log to console for debugging
  console.error('API Error:', {
    message: errorDetails.message,
    status: errorDetails.status,
    code: errorDetails.code,
    isAuthError: errorDetails.isAuthError,
    isNetworkError: errorDetails.isNetworkError,
    details: errorDetails.details,
    originalError: error,
  });

  // Show notification to user (unless silent)
  if (!silent) {
    if (errorDetails.isAuthError) {
      toast.error(errorDetails.message, {
        position: 'top-center',
        autoClose: 5000,
        toastId: 'auth-error', // Prevent duplicate auth errors
      });

      // Redirect to login after delay
      if (redirectOnAuth) {
        setTimeout(() => {
          // Clear auth tokens only
          removeAuthToken();

          // Redirect to login
          window.location.href = '/login';
        }, 2000);
      }
    } else if (errorDetails.isNetworkError) {
      toast.error(errorDetails.message, {
        position: 'top-right',
        autoClose: 5000,
        toastId: 'network-error',
      });
    } else {
      const message =
        showDetails && errorDetails.details
          ? `${errorDetails.message}\n${JSON.stringify(errorDetails.details)}`
          : errorDetails.message;

      toast.error(message, {
        position: 'top-right',
        autoClose: 5000,
      });
    }
  }

  return errorDetails;
}

/**
 * Wrapper for API calls with automatic error handling
 */
export async function withErrorHandling<T>(
  apiCall: () => Promise<T>,
  errorMessage?: string,
  options?: {
    silent?: boolean;
    showDetails?: boolean;
    redirectOnAuth?: boolean;
  }
): Promise<T | null> {
  try {
    return await apiCall();
  } catch (error) {
    handleApiError(error, errorMessage, options);
    return null;
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: any): boolean {
  const errorDetails = parseApiError(error);

  // Retry network errors and 5xx server errors
  return (
    errorDetails.isNetworkError || (errorDetails.status !== undefined && errorDetails.status >= 500)
  );
}

/**
 * Retry API call with exponential backoff
 */
export async function retryApiCall<T>(
  apiCall: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;

      // Don't retry if not retryable or last attempt
      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      const delay = initialDelay * Math.pow(2, attempt);
      console.log(`Retrying API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
